import { useEffect, useMemo, useRef, useState } from "react";
import { currentShift } from "@/lib/shift";
import { getHeld, openShiftDialog, postDisplay, saveHeld, useHotkeys, closeAllWindows } from "@/lib/tpv";
import HeldDialog from "@/components/tpv/HeldDialog";
import useManagerGate from "@/components/tpv/ManagerGate";
import KeyboardButton from "@/components/tpv/OnScreenKeyboard";
import { uuid } from "@/lib/format";
import TpvKeypad from "@/components/pos/TpvKeypad";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, ShoppingCart, ScanLine, Scale, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import ScaleButton from "@/components/scale/ScaleButton";
import WeighPanel from "@/components/scale/WeighPanel";
import useScannerInput from "@/lib/useScannerInput";
import { parseScaleBarcode, round3, weighPrice, weighedLine } from "@/lib/weigh";
import { toast } from "sonner";
import BarcodeScanner from "@/components/BarcodeScanner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useApp } from "@/context/AppContext";
import { categoriesFor, ProductIcon, catColor, iconColor, hasPhoto, MEDICAL_SHOP, furnitureSpec } from "@/lib/catalog";
import ERxDialog from "@/components/pos/ERxDialog";
import TopUpDialog from "@/components/pos/TopUpDialog";
import { VehicleFilter, VehicleBadge, vehicleMatch, vehicleText } from "@/components/inventory/VehicleFilter";
import { Signal, Sofa } from "lucide-react";
import ConfiguratorDialog from "@/components/furniture/ConfiguratorDialog";
import { ClipboardPlus } from "lucide-react";
import { fmtNum, isLow, round2, vals } from "@/lib/format";
import { BigButton, Chip, Empty } from "@/components/ui-kit";
import { CharityDialog, charityLine } from "@/components/pos/CharityDialog";
import CartPanel from "@/components/pos/CartPanel";
import CheckoutDialog from "@/components/pos/CheckoutDialog";
import SplitDialog from "@/components/pos/SplitDialog";
import FavEditor from "@/components/pos/FavEditor";
import { favItems, favPages } from "@/lib/favorites";
import { Star, Pencil } from "lucide-react";
import Receipt from "@/components/Receipt";

function ProductTile({ p, onAdd, inCart, lowDefault, color }) {
  const low = isLow(p, lowDefault);
  const cc = catColor(p.category);
  return (
    <button
      type="button"
      onClick={() => onAdd(p)}
      data-testid="pos-quick-product-btn"
      data-product-id={p.id}
      style={{ "--cc": cc, ...(color ? { background: color } : {}) }}
      className={`pos-tile relative min-h-[132px] p-3 rounded-2xl text-start flex flex-col gap-2 border transition-[transform,box-shadow,border-color] hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.97] ${inCart > 0 ? "border-[var(--cc)] ring-2 ring-[var(--cc)]/30" : "border-border/70"}`}
    >
      <span className="pos-tile-glow" aria-hidden="true" />
      {hasPhoto(p) ? (
        <span className="relative w-full h-24 rounded-xl overflow-hidden bg-black/5 shadow-md" data-testid="pos-tile-photo"><ProductIcon product={p} /></span>
      ) : (
        <span className="relative w-12 h-12 icon-glass grid place-items-center" style={{ "--ic": p.icon ? iconColor(p.icon) : cc }}><ProductIcon product={p} plain /></span>
      )}
      <span className="relative font-bold leading-snug line-clamp-2 text-sm sm:text-base text-[#1A2733]">{p.name}</span>
      {!!furnitureSpec(p) && <span className="relative text-[11px] text-muted-foreground line-clamp-1" dir="ltr" data-testid="pos-tile-spec">{furnitureSpec(p)}</span>}
      <VehicleBadge p={p} />
      <span className="relative mt-auto flex items-end justify-between gap-1">
        <span className="text-lg font-black tracking-tight flex items-center gap-1" style={{ color: cc }} dir="ltr">{fmtNum(p.price)}{p.sold_by_weight && <span className="text-xs font-bold text-muted-foreground flex items-center gap-0.5" data-testid="pos-weighed-badge"><Scale className="w-3.5 h-3.5" />/kg</span>}{p.price_per_m2 && <span className="text-xs font-bold text-muted-foreground" data-testid="pos-m2-badge">/m²</span>}</span>
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${low ? "bg-[#C7353B] text-white" : "bg-black/5 text-[#555]"}`}>{fmtNum(p.stock)}</span>
      </span>
      {inCart > 0 && <span className="absolute top-2 end-2 min-w-7 h-7 px-1 rounded-full text-white text-sm font-black grid place-items-center shadow" style={{ background: cc }}>{fmtNum(inCart)}</span>}
    </button>
  );
}

function useIsDesktop() {
  const mq = "(min-width: 1024px)";
  const [on, setOn] = useState(() => window.matchMedia(mq).matches);
  useEffect(() => {
    const m = window.matchMedia(mq);
    const fn = (e) => setOn(e.matches);
    m.addEventListener("change", fn);
    return () => m.removeEventListener("change", fn);
  }, []);
  return on;
}

export default function Sell() {
  const isDesktop = useIsDesktop();
  const { view, t, money, can, dispatch, lang } = useApp();
  const navigate = useNavigate();
  const [weighing, setWeighing] = useState(null);
  const [carpet, setCarpet] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const addLines = (lines) => setCart((c) => { let n = [...c]; lines.forEach((l) => { const i = n.findIndex((x) => x.product_id === l.product_id && !x.note); n = i >= 0 ? n.map((x, k) => (k === i ? { ...x, qty: round2(x.qty + l.qty) } : x)) : [...n, l]; }); return n; });
  const [cw, setCw] = useState("");
  const [cl, setCl] = useState("");
  const carpetArea = Math.round((Number(cw) || 0) * (Number(cl) || 0) * 100) / 100;
  const addCarpet = () => {
    if (!carpetArea) return;
    const p = carpet;
    setCart((c) => [...c, { product_id: p.id, name: `${p.name} ${cw}×${cl} m (${carpetArea} m²)`, price: round2(p.price * carpetArea), cost: round2((p.cost || 0) * carpetArea), qty: 1, m2: carpetArea }]);
    setCarpet(null); setCw(""); setCl("");
  };
  const [erxOpen, setErxOpen] = useState(false);
  const [erx, setErx] = useState(null);
  const [topupOpen, setTopupOpen] = useState(false);
  const isAuto = view.shop?.type === "autoparts";
  const [vehicle, setVehicle] = useState({ make: "", model: "" });
  const [q, setQ] = useState("");
  const pages = favPages(view);
  const [cat, setCat] = useState(() => (pages[0] ? `fav:${pages[0].id}` : "all"));
  const [favEdit, setFavEdit] = useState(false);
  const [cart, setCart] = useState([]);
  const [promoId, setPromoId] = useState(null);
  const [mode, setMode] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [sel, setSel] = useState(null);
  const [ticketDisc, setTicketDisc] = useState(0);
  const [held, setHeld] = useState(getHeld);
  const [heldOpen, setHeldOpen] = useState(false);
  const [gate, gateDialog] = useManagerGate();
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("recall")) { setHeld(getHeld()); setHeldOpen(true); setParams({}, { replace: true }); }
    if (params.get("vehicle")) {
      const c = view.customers?.[params.get("customer")]; const v = (c?.vehicles || []).find((x) => x.id === params.get("vehicle"));
      if (v) setVehicle({ make: v.make || "", model: "", plate: v.plate, id: v.id, customer_id: c.id, customer_name: c.name, km: v.km || "" });
      setParams({}, { replace: true });
    }
  }, [params, setParams, view.customers]);
  const searchRef = useRef(null);
  const shift = currentShift(view);

  const favPage = !q && cat.startsWith("fav:") ? pages.find((p) => `fav:${p.id}` === cat) : null;
  const products = useMemo(() => (favPage ? favItems(view, favPage) : vals(view.products)
    .filter((p) => !p.deleted && (cat === "all" || cat.startsWith("fav:") || p.category === cat) && (!isAuto || vehicleMatch(p, vehicle)) && `${p.name} ${p.barcode || ""} ${p.active_ingredient || ""} ${isAuto ? vehicleText(p) : ""}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name)).map((p) => ({ p }))), [view, cat, q, favPage, isAuto, vehicle]);

  const add = (p) => (p.sold_by_weight ? setWeighing(p) : p.price_per_m2 ? setCarpet(p) : setCart((c) => (c.some((l) => l.product_id === p.id)
    ? c.map((l) => (l.product_id === p.id ? { ...l, qty: l.qty + 1 } : l))
    : [...c, { product_id: p.id, name: p.name, price: p.price, cost: p.cost || 0, qty: 1, ...(p.warranty_months ? { warranty_months: Number(p.warranty_months) } : {}) }])));
  const addWeighed = (p, kg, total) => setCart((c) => {
    const old = c.find((l) => l.product_id === p.id);
    if (!old) return [...c, weighedLine(p, kg, total)];
    const nk = round3(old.qty + kg);
    return c.map((l) => (l === old ? weighedLine(p, nk, round2(old.price * old.qty + total)) : l));
  });
  const byBarcode = (code) => vals(view.products).find((p) => !p.deleted && p.barcode && p.barcode === code);
  const resolve = (code) => {
    const p = byBarcode(code);
    if (p) { add(p); return p.name; }
    const w = parseScaleBarcode(code, view.shop, view.products);
    if (w) { addWeighed(w.product, w.kg, w.total); return `${w.product.name} · ${w.kg.toFixed(3)} kg · ${money(w.total)}`; }
    return null;
  };
  const onCode = (code) => {
    const name = resolve(code);
    if (name) { toast.success(name); return; }
    toast.error(`${t("not_found_barcode")}: ${code}`, can("inventory") ? {
      action: { label: t("add_product"), onClick: () => navigate(`/inventory?barcode=${encodeURIComponent(code)}`) },
    } : undefined);
  };
  const onSearchKey = (e) => {
    if (e.key !== "Enter" || !q.trim()) return;
    if (resolve(q.trim())) setQ("");
  };
  useScannerInput(onCode, !mode && !weighing && !scanning && !receipt);
  const applyLine = (l, patch) => {
    const n = { ...l, ...patch };
    const unit = n.ppk ? weighPrice(n.qty, n.ppk, view.shop) / n.qty : (n.base ?? l.price);
    return { ...n, ...(n.ppk ? {} : { base: unit }), price: unit * (1 - (Number(n.disc) || 0) / 100) };
  };
  const patchLine = (id, patch) => setCart((c) => c.map((l) => (l.product_id === id ? applyLine(l, patch) : l)));
  const setQty = (id, qty) => {
    if (qty <= 0 && id === sel) setSel(null);
    setCart((c) => (qty <= 0 ? c.filter((l) => l.product_id !== id) : c.map((l) => (l.product_id === id ? applyLine(l, { qty }) : l))));
  };
  const onLineDisc = (l, x) => gate(() => patchLine(l.product_id, { disc: x }));
  const onPrice = (l, x) => gate(() => patchLine(l.product_id, l.ppk ? { ppk: x } : { base: x }));
  const onTicketDisc = (x) => gate(() => setTicketDisc(x));
  const storeHeld = (list) => { setHeld(list); saveHeld(list); };
  const resetSale = () => { setCart([]); setPromoId(null); setTicketDisc(0); setSel(null); setErx(null); setCharityDeclined(false); };
  const hold = () => {
    if (!cart.length) return;
    storeHeld([{ id: uuid(), at: new Date().toISOString(), label: `#${held.length + 1}`, cart, promoId, ticketDisc }, ...held]);
    resetSale();
    toast.success(t("held_saved"));
  };
  const recall = (h) => {
    const rest = held.filter((x) => x.id !== h.id);
    storeHeld(cart.length ? [{ id: uuid(), at: new Date().toISOString(), label: `#${rest.length + 1}`, cart, promoId, ticketDisc }, ...rest] : rest);
    setCart(h.cart); setPromoId(h.promoId || null); setTicketDisc(h.ticketDisc || 0); setSel(null); setHeldOpen(false);
    toast.success(t("recalled"));
  };

  const promo = promoId ? view.promotions[promoId] : null;
  const subtotal = round2(cart.reduce((s, l) => s + l.price * l.qty, 0));
  const discPct = (promo?.active ? Number(promo.percent) : 0) + Number(ticketDisc || 0);
  const discount = Math.min(subtotal, round2((subtotal * discPct) / 100));
  const totals = { subtotal, discount, total: round2(subtotal - discount), ticketDisc };
  const count = cart.reduce((s, l) => s + (l.ppk ? 1 : l.qty), 0);

  const done = (sale) => {
    postDisplay({ type: "done", sale_id: sale.id, lang, total: round2(sale.total + (sale.charity || 0)), change: sale.given > sale.total + (sale.charity || 0) ? round2(sale.given - sale.total - (sale.charity || 0)) : 0, currency: view.shop?.currency, shop: view.shop?.name });
    setReceipt(sale); resetSale(); setMode(null); setCartOpen(false);
  };
  const [charityAsk, setCharityAsk] = useState(null);
  const [charityDeclined, setCharityDeclined] = useState(false);
  const charityAmt = Number(view.shop?.charity_amount) || 0;
  const checkout = (m) => {
    if (!cart.length) return;
    if (!shift) { toast.error(t("no_shift_banner")); openShiftDialog(); return; }
    if (charityAmt > 0 && !charityDeclined && !cart.some((l) => l.charity)) { setCharityAsk(m); return; }
    setMode(m);
  };
  const charityAccept = () => { setCart((c) => [...c, charityLine(view.shop, t)]); const m = charityAsk; setCharityAsk(null); setMode(m); };
  const charityDecline = () => { setCharityDeclined(true); const m = charityAsk; setCharityAsk(null); if (m) setMode(m); };
  useEffect(() => {
    if (!charityAsk) return;
    postDisplay({ type: "charity", amount: charityAmt, currency: view.shop?.currency, shop: view.shop?.name, name: view.shop?.charity_name || "", total: totals.total });
  }, [charityAsk, charityAmt, totals.total, view.shop]);
  const idle = !mode && !weighing && !scanning && !receipt && !heldOpen && !charityAsk;
  useHotkeys({
    F2: () => searchRef.current?.focus(), F11: () => setScanning(true), F4: () => { if (!closeAllWindows()) checkout("cash"); }, F8: () => checkout("credit"),
    F5: hold, "Shift+F5": () => setHeldOpen(true),
    Delete: () => { const l = cart.find((x) => x.product_id === sel) || cart[cart.length - 1]; if (l) setQty(l.product_id, 0); },
  }, idle);
  useEffect(() => {
    if (receipt || charityAsk) return;
    postDisplay({
      type: "cart", currency: view.shop?.currency, shop: view.shop?.name, total: totals.total, discount: totals.discount,
      lines: cart.map((l) => ({ name: l.name, qty: l.qty, kg: !!l.ppk, amount: round2(l.price * l.qty), charity: !!l.charity })),
    });
  }, [cart, totals.total, totals.discount, receipt, view.shop, charityAsk]);
  const panel = (
    <div className="space-y-3">
      <CartPanel cart={cart} setQty={setQty} totals={totals} promoId={promoId} setPromoId={setPromoId} onTicketDisc={onTicketDisc}
        onCheckout={checkout} onClear={resetSale} sel={sel} setSel={setSel}
        onHold={hold} onRecall={() => setHeldOpen(true)} heldCount={held.length} />
      {cart.length > 0 && <TpvKeypad cart={cart} sel={sel} setQty={setQty} onLineDisc={onLineDisc} onPrice={onPrice} onTicketDisc={onTicketDisc} />}
    </div>
  );

  return (
    <div data-testid="pos-sale-screen" className="lg:grid lg:grid-cols-[1fr_400px] [.touch-mode_&]:xl:grid-cols-[1fr_460px] lg:gap-4">
      <div>
        {!shift && (
          <button type="button" onClick={openShiftDialog} data-testid="pos-no-shift-banner"
            className="w-full mb-3 rounded-2xl bg-gradient-to-r from-[#C7353B] to-[#E9672E] text-white p-3 font-bold flex items-center justify-between gap-2 shadow-md">
            <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" />{t("no_shift_banner")}</span><span className="rounded-full bg-white text-[#C7353B] px-4 py-1.5 text-sm">{t("open_shift")} · F6</span>
          </button>
        )}
        <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-5 h-5 text-muted-foreground" />
          <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey} placeholder={`${t("search_products")}  (F2)`} data-testid="pos-search-input"
            className="w-full h-14 rounded-2xl border border-border bg-white ps-11 pe-3 text-base focus:outline-none focus:ring-2 focus:ring-primary" />
        </div>
        <KeyboardButton value={q} onChange={setQ} onEnter={() => { if (q.trim() && resolve(q.trim())) setQ(""); }} testid="pos-osk" />
        <button type="button" onClick={() => setScanning(true)} className="h-14 px-4 rounded-2xl bg-[#0B2A5B] text-white flex items-center gap-2 font-bold" data-testid="pos-scan-btn"><ScanLine className="w-6 h-6" /><span className="hidden sm:inline">{t("scan")}</span></button>
        {MEDICAL_SHOP(view.shop?.type) && <button type="button" onClick={() => setErxOpen(true)} className="h-14 px-4 rounded-2xl bg-[#C0392B] text-white flex items-center gap-2 font-bold" data-testid="pos-erx-btn"><ClipboardPlus className="w-6 h-6" /><span className="hidden sm:inline">℞</span></button>}
        {view.shop?.type === "furniture" && <button type="button" onClick={() => setCfgOpen(true)} className="h-14 px-4 rounded-2xl bg-[#8E44AD] text-white flex items-center gap-2 font-bold" data-testid="pos-configurator-btn"><Sofa className="w-6 h-6" /><span className="hidden sm:inline">{t("cfg_btn")}</span></button>}
        {view.shop?.type === "phones" && <button type="button" onClick={() => setTopupOpen(true)} className="h-14 px-4 rounded-2xl bg-[#C0392B] text-white flex items-center gap-2 font-bold" data-testid="pos-topup-btn"><Signal className="w-6 h-6" /><span className="hidden sm:inline">{t("topup")}</span></button>}
        <ScaleButton />
        </div>
        <Dialog open={!!weighing} onOpenChange={(o) => !o && setWeighing(null)}>
          <DialogContent className="max-w-md" data-testid="pos-weigh-dialog">
            <DialogTitle className="font-heading text-2xl flex items-center gap-2"><Scale className="w-6 h-6" />{weighing?.name}</DialogTitle>
            {weighing && <WeighPanel key={weighing.id} product={weighing} confirmLabel={t("add_to_cart")} confirmIcon={Plus}
              onConfirm={(kg, total) => { addWeighed(weighing, kg, total); setWeighing(null); }} />}
          </DialogContent>
        </Dialog>
        <Dialog open={!!carpet} onOpenChange={(o) => !o && setCarpet(null)}>
          <DialogContent className="max-w-md" data-testid="pos-carpet-dialog">
            <DialogTitle className="font-heading text-2xl">{carpet?.name} · <span dir="ltr">{fmtNum(carpet?.price)}/m²</span></DialogTitle>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm font-bold">{t("carpet_width")} (m)<input autoFocus dir="ltr" inputMode="decimal" value={cw} onChange={(e) => setCw(e.target.value)} className="mt-1 w-full h-14 rounded-xl border border-border px-3 text-2xl font-black" data-testid="carpet-w" /></label>
              <label className="text-sm font-bold">{t("carpet_length")} (m)<input dir="ltr" inputMode="decimal" value={cl} onChange={(e) => setCl(e.target.value)} className="mt-1 w-full h-14 rounded-xl border border-border px-3 text-2xl font-black" data-testid="carpet-l" /></label>
            </div>
            <div className="rounded-xl bg-secondary p-4 flex items-center justify-between"><span className="font-bold" dir="ltr" data-testid="carpet-area">{carpetArea} m²</span><span className="text-3xl font-black text-[#1F9D55]" dir="ltr" data-testid="carpet-total">{fmtNum(round2((carpet?.price || 0) * carpetArea))}</span></div>
            <BigButton onClick={addCarpet} disabled={!carpetArea} className="w-full" data-testid="carpet-add"><Plus className="w-5 h-5" />{t("add_to_cart")}</BigButton>
          </DialogContent>
        </Dialog>
        <CharityDialog open={!!charityAsk} amount={charityAmt} currency={view.shop?.currency} onAccept={charityAccept} onDecline={charityDecline} />
        <BarcodeScanner open={scanning} onClose={() => setScanning(false)} onDetected={onCode} />
        {isAuto && <div className="mb-3"><VehicleFilter products={vals(view.products).filter((p) => !p.deleted)} value={vehicle} onChange={setVehicle} testid="pos-vehicle" customers={view.customers} /></div>}
        <div className="xl:grid xl:grid-cols-[160px_1fr] xl:gap-3">
        <div className="pos-cats flex xl:flex-col gap-2 overflow-x-auto xl:overflow-visible no-scrollbar pb-3 -mx-1 px-1 xl:mx-0 xl:px-0 xl:self-start xl:sticky xl:top-20">
          {pages.map((pg) => (
            <Chip key={pg.id} active={cat === `fav:${pg.id}`} onClick={() => setCat(`fav:${pg.id}`)} data-testid="pos-fav-page"><Star className="w-4 h-4 text-[#E3A008]" fill="#E3A008" />{pg.name}</Chip>
          ))}
          {can("inventory") && <Chip onClick={() => setFavEdit(true)} data-testid="pos-fav-edit"><Pencil className="w-4 h-4" />{t("fav_edit")}</Chip>}
          <Chip active={cat === "all"} onClick={() => setCat("all")} data-testid="pos-cat-all">{t("all")}</Chip>
          {categoriesFor(view.shop?.type).map(({ key, icon: Icon, color: cc }) => (
            <Chip key={key} active={cat === key} onClick={() => setCat(key)} data-testid={`pos-cat-${key}`} className="pos-cat-chip" style={{ "--cc": cc, ...(cat === key ? { background: cc, borderColor: cc } : {}) }}><span className="w-6 h-6 rounded-md grid place-items-center text-white shrink-0" style={{ background: cc }}><Icon className="w-3.5 h-3.5" /></span>{t(`cat_${key}`)}</Chip>
          ))}
        </div>
        {products.length === 0 ? <Empty icon={ShoppingCart} text={favPage ? t("fav_empty") : t("no_products")} testid="pos-empty" /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
            {products.map(({ p, color }) => <ProductTile key={p.id} p={p} color={color} onAdd={add} lowDefault={view.shop?.low_stock_default}
              inCart={cart.find((l) => l.product_id === p.id)?.qty || 0} />)}
          </div>
        )}
        </div>
      </div>
      {isDesktop && <aside className="sticky top-20 self-start max-h-[calc(100vh-8rem)] overflow-y-auto no-scrollbar">{panel}</aside>}

      {!isDesktop && count > 0 && (
        <button type="button" onClick={() => setCartOpen(true)} data-testid="pos-open-cart-btn"
          className="lg:hidden fixed bottom-[76px] inset-x-3 z-30 h-16 rounded-2xl bg-foreground text-white px-4 flex items-center justify-between shadow-xl rise">
          <span className="flex items-center gap-2 font-bold"><ShoppingCart className="w-6 h-6" />{count} {t("items")}</span>
          <span className="text-xl font-extrabold" data-testid="pos-bar-total">{money(totals.total)}</span>
        </button>
      )}
      <Sheet open={!isDesktop && cartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-3xl p-4">
          <SheetTitle className="sr-only">{t("cart")}</SheetTitle>
          {panel}
        </SheetContent>
      </Sheet>
      <CheckoutDialog gate={gate} mode={mode === "split" ? null : mode} onClose={() => setMode(null)} cart={cart} totals={totals} promo={promo} onDone={done} rxPrefill={erx} initialCustomer={vehicle.customer_id || null} extra={vehicle.id ? { vehicle: { id: vehicle.id, plate: vehicle.plate, make: vehicle.make, model: vehicle.model, km: vehicle.km ? Number(vehicle.km) : null } } : null} />
      {cfgOpen && <ConfiguratorDialog onClose={() => setCfgOpen(false)} onAdd={addLines} />}
      <TopUpDialog open={topupOpen} onClose={() => setTopupOpen(false)} onAdd={(line) => setCart((c) => [...c, line])} />
      <ERxDialog open={erxOpen} onClose={() => setErxOpen(false)} onUse={({ rx, products, missing }) => { products.forEach((p) => add(p)); setErx(rx); if (missing.length) toast.warning(`${t("erx_no_stock")}: ${missing.join(", ")}`); else toast.success(`℞ ${rx.code}`); }} />
      <SplitDialog open={mode === "split"} onClose={() => setMode(null)} cart={cart} totals={totals} promo={promo} onDone={done} />
      <FavEditor open={favEdit} onClose={() => setFavEdit(false)} />
      <Receipt sale={receipt} onClose={() => { setReceipt(null); postDisplay({ type: "clear" }); }} fresh />
      <HeldDialog open={heldOpen} onClose={() => setHeldOpen(false)} held={held} onRecall={recall} onDelete={(id) => storeHeld(held.filter((h) => h.id !== id))} />
      {gateDialog}
    </div>
  );
}
